import { useState, useEffect, useCallback } from "react"

import AssetReservationPaged from "models/AssetReservationPaged"
import AssetService, { IGetAssetReservationsParams } from "services/AssetService"
import { IMultipleEntities } from "@/types"

export default function useAssetReservations(reservationsParams?: IGetAssetReservationsParams, onFail?: () => void) {
    const [assetReservations, setAssetReservations] = useState<AssetReservationPaged[] | null>(null)
    const [loadNextPageCall, setLoadNextPageCall] = useState<(() => Promise<IMultipleEntities<AssetReservationPaged> | null>) | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)

    const hasMore = !!loadNextPageCall

    const fetchAssetReservations = useCallback(async (params: IGetAssetReservationsParams) => {
        setIsLoading(true)
        setAssetReservations(null)

        const fetchedAssetReservations = await AssetService.getAssetReservations(params)

        setAssetReservations(fetchedAssetReservations?.list || null)
        setLoadNextPageCall(fetchedAssetReservations?.loadMore ? () => fetchedAssetReservations.loadMore! : null)
        setIsLoading(false)
        setIsInitialLoad(false)

        if (!fetchedAssetReservations?.list && typeof onFail === "function") {
            onFail()
        }
    }, [onFail])

    const loadNextPage = useCallback(async() => {
        setLoadNextPageCall(null)
        if (!loadNextPageCall) return
        setIsLoading(true)
        const nextReservations = await loadNextPageCall()
        setIsLoading(false)
        setAssetReservations([...assetReservations || [], ...nextReservations?.list || []])
        setLoadNextPageCall(nextReservations?.loadMore ? () => nextReservations.loadMore! : null)
    }, [assetReservations, loadNextPageCall])

    useEffect(() => {
        if (!reservationsParams) {
            setAssetReservations(null)
            setIsLoading(false)
            return
        }
        fetchAssetReservations(reservationsParams)
    }, [reservationsParams, fetchAssetReservations])

    const reload = useCallback(() => {
        if (!reservationsParams) {
            return
        }
        fetchAssetReservations(reservationsParams)
    }, [reservationsParams, fetchAssetReservations])

    return {
        assetReservations,
        isInitialLoad,
        hasMore,
        isLoading,
        loadNextPage,
        reload,
    }
}
